import {
  Dropdown,
  FilterValueModel,
  ValidationMessage,
} from "components/library"
import React, { useState } from "react"
import { OptionModel } from "components/helpers"
import { useTranslation } from "react-i18next"
import { useCompanies } from "queries"
import { MIN_SEARCH_LETTERS } from "queries/masterData/hooks/useMasterData"
import { QueryFilterModel } from "../dropdownEbate/query.model"
import { StyledCompanyDropdown } from "./styled"

const FILTER_SENTINEL: any[] = [{ value: null, title: "" }]

function getDropdownOptions(
  isLoading: boolean,
  list: any[],
  currentFilterText: string
): any[] {
  if (list.length > 0) return list

  if (currentFilterText.length > 0) return FILTER_SENTINEL
  return []
}
const bypassLocalFilter = () => true

interface Props {
  companySelected?: FilterValueModel<number>
  companyOnChange: (e: any, description) => void
  errorMessage: string
  displayValidations?: boolean
  heading?: string
  required?: boolean
  allCompaniesActive?: boolean
  hidden?: boolean
  componenttype?
  label?: string
  name?: string
  width?: string
  isLoading?: boolean
}

// To use this component when a company is already selected we have to put the value and the name of the company inside
// the companySelected property
export const CompanyDropdown: React.FC<Props> = ({
  companySelected,
  companyOnChange,
  errorMessage = "",
  displayValidations = false,
  name,
  heading = "",
  required,
  allCompaniesActive,
  componenttype,
  hidden,
  label,
  width,
  isLoading,
}) => {
  const packagesLang = "packages"
  const { t } = useTranslation(["translation", packagesLang])

  const [query, setQuery] = useState<QueryFilterModel>({ query: "" })
  const companies = useCompanies(query, allCompaniesActive)
  const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(false)

  const [searchTimeout, setSearchTimeout] = useState<any>()
  const [filterText, setFilterText] = useState<string>("")

  const [companyList, setCompanyList] = useState<OptionModel[]>([])

  const filterByText = (textToFilter: string) => {
    setQuery({ query: textToFilter })
  }

  const textChanged = (event: any) => {
    const val: string = event.filter ?? ""
    setFilterText(val)

    if (val === "") {
      clearTimeout(searchTimeout)
      setIsLoadingCompanies(false)
      filterByText("")
      return
    }

    if (val !== "" || query.query === "") {
      setIsLoadingCompanies(true)
      clearTimeout(searchTimeout)
      setSearchTimeout(
        setTimeout(() => {
          filterByText(val)
          setIsLoadingCompanies(false)
        }, 200)
      )
    }
  }

  React.useEffect(() => {
    if (filterText.length < MIN_SEARCH_LETTERS) {
      if (
        companySelected?.value !== undefined &&
        companySelected?.value !== null
      ) {
        setCompanyList([
          {
            value: companySelected.value,
            title: companySelected.title ?? "",
          },
        ])
      } else {
        setCompanyList([])
      }
    }
  }, [filterText])

  React.useEffect(() => {
    if (!companies.isLoading) {
      if (
        companySelected?.value !== undefined &&
        companySelected?.value !== null &&
        !companies.data.map((c) => c.value).includes(companySelected?.value)
      ) {
        const options = companies.data.concat([
          {
            value: companySelected.value,
            title: companySelected.title,
          },
        ])

        setCompanyList(options)
      } else {
        setCompanyList(companies.data)
      }
    }
  }, [companies.data, companies.isLoading])

  const getEmptyFilterMessage = () => {
    if (isLoadingCompanies && filterText.length >= MIN_SEARCH_LETTERS)
      return t("translation:loading")
    if (filterText.length < MIN_SEARCH_LETTERS)
      return t(`${packagesLang}:filterByNameHint`)
    return t("translation:no-records-found")
  }

  const baseOptions = getDropdownOptions(
    companies.isLoading || isLoadingCompanies,
    companyList,
    filterText
  )

  const computedOptions =
    companySelected?.value !== undefined &&
      companySelected?.value !== null &&
      !baseOptions.some((o) => o.value === companySelected.value)
      ? [
        ...baseOptions.filter((o) => o.value !== null),
        { value: companySelected.value, title: companySelected.title ?? "" },
      ]
      : baseOptions

  return (
    <>
      <StyledCompanyDropdown />

      <Dropdown
        panelClassName="company-dropdown-panel"
        heading={heading}
        options={computedOptions}
        id="company"
        name={name}
        value={companySelected?.value}
        required={required}
        placeholder={t("select-option-placeholder")}
        showLoadingTemplate={false}
        width={width}
        onChange={(e) => {
          if (e.target.value === undefined || e.target.value === null) {
            setFilterText("")
            filterByText("")
            setCompanyList([])
            companyOnChange(e, undefined)
            return
          }

          const description = companyList?.find(
            (o) => o.value === e.target.value
          )?.title
          companyOnChange(e, description)
        }}
        onFilter={(e) => {
          textChanged(e)
        }}
        canSearch
        hidden={hidden}
        filterPlaceholder={t("searchForMoreCompanies")}
        componenttype={componenttype}
        label={label}
        emptyFilterMessage={getEmptyFilterMessage()}
        filterFunction={bypassLocalFilter}
        noResetFilterOnHide={false}
        onHide={() => {
          setFilterText("")
          setCompanyList([])
          filterByText("")
        }}
        filterValue={filterText}
        onFilterValueChange={(e) => setFilterText(e.value ?? "")}
        resetFilterOnClear
        overlayVisible={
          filterText.length > 0
            ? filterText.length >= MIN_SEARCH_LETTERS
            : undefined
        }
      />
      <ValidationMessage
        messageError={errorMessage}
        showMessage={
          displayValidations &&
          errorMessage !== undefined &&
          errorMessage !== null &&
          errorMessage.length > 0
        }
      />
    </>
  )
}
